import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, startWith } from 'rxjs/operators';
import { IIDN } from 'src/app/shared/models/IIDN';
import { ICountry, IState } from 'src/app/shared/models/lookup.model';
import { ResourcesService } from 'src/app/shared/services/resources.service';
import { IdnNameValidator } from '../shared/idn-name.validator';
import { IdnService, OperationTypeIdn } from '../shared/idn.service';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { OnlySpacesValidator } from 'src/app/shared/util/onlySpaces.validator';
import { ContainsSpaceValidator } from 'src/app/shared/util/containsSpace.validator';
import { ITenantIDM } from 'src/app/shared/models/ITenantIDM';

@Component({
  selector: 'app-add-idn',
  templateUrl: './add-idn.component.html',
  styleUrls: ['./add-idn.component.css']
})

export class AddIdnComponent implements OnInit {
  btnCreateText = "";
  btnCancelText = "";
  lblCreateText = "";
  isLoading = false;
  zipPattern: any = "^[0-9]{5}-[0-9]{4}?$";
  allIDNs: IIDN[];
  allTenantsIDM: ITenantIDM[];
  allCountries: ICountry[];
  allStates: IState[];
  createMode: boolean;
  isEdit: boolean;
  selectedCountry: number = 0;
  selectedStateValue: string = '0';
  selectedTentatIDMValue: string = '';
  selectedIdnGuid: string = "";
  resourceKeyValue: any;
  resource: string = ""
  successMessageOnCreate = ""
  successMessageOnUpdate = ""
  errorMessageOnCreate = ""
  errorMessageOnUpdate = ""
  operationTypeFacility = OperationTypeIdn;

  operationTypeForm = this.fb.group({
    operationType: [OperationTypeIdn.Select, [Validators.required]],
  });

  idnInfoForm = this.fb.group({
    id: ['-1', []],
    idnName: [{value: '', disabled: true}],
    name: ['', [Validators.maxLength(200), OnlySpacesValidator.onlyWhiteSpace]],
    idnCode: [{value: '', disabled: true}, [Validators.maxLength(200), ContainsSpaceValidator.containsWhiteSpace]],
    accountNumber: ['', [Validators.maxLength(255), ContainsSpaceValidator.containsWhiteSpace]],
    countryId: ['0', []],
    address1: ['', [Validators.maxLength(255), OnlySpacesValidator.onlyWhiteSpace]],
    address2: ['', [Validators.maxLength(255), OnlySpacesValidator.onlyWhiteSpace]],
    city: ['', [Validators.maxLength(255), OnlySpacesValidator.onlyWhiteSpace]],
    state: ['', []],
    zipCode: [undefined, [Validators.maxLength(10), Validators.pattern(this.zipPattern)]]
  });

  @ViewChild(ToastContainerDirective, { static: true })
  toastContainer: ToastContainerDirective;

  filteredOptions: Observable<string[]>;

  constructor(
    private idnService: IdnService,
    private router: Router,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private resourcesService: ResourcesService,
    public toastrService: ToastrService
  ) { }

  ngOnInit(): void {

    this.isLoading = true;
    this.toastrService.overlayContainer = this.toastContainer;
    this.resourcesService.bsObject.subscribe(result => {
      result && (this.resourceKeyValue = result);
      this.resource = this.resourceKeyValue.resourceKeyIDN;
    });

    this.idnService.getAllCountry()
      .pipe(
        mergeMap((countries: any) => {
          this.allCountries = countries
          return this.idnService.getAllFromIDM()
        }),
        catchError(error => {
          console.log(error)
          return of([])
        }),
        mergeMap((IDMTenants: any) => {
          this.allTenantsIDM = IDMTenants
          return this.idnService.getAllIDNs();
        }),
        catchError(error => {
          console.log(error)
          return of([])
        }),
        mergeMap((IDNs: any) => {
          this.allIDNs = IDNs
          return this.route.params
        }),
        mergeMap((params: any) => {
          return params['id'] ? this.idnService.getIDNById(parseInt(params['id'], 10)) : of({});
        })
      ).subscribe((response: IIDN) => {
        if (response && Object.keys(response).length > 0) {
          this.isEdit = true;
          this.createMode = false;
          this.populateIdnInfoForm(response);
        } else {
          this.isEdit = false;
          this.createMode = true;
          this.setFieldValue('name', this.resourceKeyValue.selectIdnName);

          this.idnInfoForm.controls.name.setValidators([
            Validators.required,
            Validators.maxLength(200),
            IdnNameValidator.ValidateName(this.allIDNs),
            IdnNameValidator.ValidateIDN(this.allTenantsIDM),
            OnlySpacesValidator.onlyWhiteSpace
          ]);

          this.filteredOptions = this.idnInfoForm.get('name')!.valueChanges
            .pipe(
              startWith(''),
              map(value => this._filter(value)),
            );

          this.isLoading = false;
        }
        this.setControlTextValues();
      });

  }

  private _filter(value: string): any[] {
    const filterValue = value ? value.toLowerCase() : "";
    let selectedTenant = this.allTenantsIDM.filter((option: any) => option.name.toLowerCase().includes(filterValue));

    if (selectedTenant.length) {
      this.selectIDMIdnId(filterValue);
      return selectedTenant
    }

    return [];
  }

  onSubmit() {
    this.isLoading = true;
    let idnRequest = this.createRequestObject(this.idnInfoForm);

    this.idnService.createIDN(idnRequest).subscribe(response => {
      if (response) {
        this.router.navigate(['/idns']);
        this.isLoading = false;
        if (this.createMode) {
          this.showSuccessNotification(this.successMessageOnCreate);
        } else {
          this.showSuccessNotification(this.successMessageOnUpdate);
        }
      } else {
        throw new Error('Failed to create IDN');
      }
    }, (error: HttpErrorResponse) => {
      this.setControlTextValues();
      this.isLoading = false;
      //TODO: Add calling AI here.
      console.error(error);
      if (this.createMode) {
        this.showErrorNotification(this.errorMessageOnCreate);
      } else {
        this.showErrorNotification(this.errorMessageOnUpdate);
      }
    });
  }

  populateIdnInfoForm(idnData: IIDN): void {
    //Get the current GUID Id Tenant from IDM to manage create/update/assignClient requests in IDM by Web API
    this.selectedIdnGuid = idnData?.idmIdnId;
    //Validate countryId against Idn country
    const countryId = this.allCountries?.find(i => i.id === idnData?.countryId);
    //Select the country value in combobox
    this.selectedCountry = countryId?.id || 0;
    //Get the states from the countryId from IDN
    let countryIdIDN = idnData?.countryId || 0;

    this.idnService.getAllStatesByCountryId(countryIdIDN)
      .subscribe(response => {
        if (response.length > 0) {
          this.allStates = response;
          const state = this.allStates?.find(i => i.code === idnData?.state);
          this.selectedStateValue = state?.code || "0";
        } else {
          if (countryIdIDN != 0) {
            this.allStates = [{ id: 1, code: 'N/A', countryId: 0, name: 'N/A' }]
            this.setFieldValue('state', this.allStates[0]);
          }
        }

        this.idnInfoForm.patchValue({
          id: idnData.id,
          idnName: idnData.name,
          name: idnData.name,
          idnCode: idnData.idmIdnCode,
          accountNumber: idnData.accountNumber,
          address1: idnData.address1,
          address2: idnData.address2,
          city: idnData.city,
          zipCode: idnData.zipcode
        });

        this.isLoading = false;
      }, (error) => {
        this.setControlTextValues();
        console.error(error);
      });

  }

  setFieldValue(fieldName: string, fieldValue: any) {
    let val = this.idnInfoForm.get(fieldName);
    if (val) {
      val.setValue(fieldValue);
    }
  }

  selectCountry(idx) {
    // get all state  based on country
    this.getAllStatesByCountryId(idx);
  }

  getAllStatesByCountryId(id: number): any {
    this.idnService.getAllStatesByCountryId(id)
      .subscribe((response: IState[]) => {
        if (response.length > 0) {
          this.allStates = response;
          this.selectedStateValue = "0";
        } else if (id <= 0) {
          this.allStates = [{ id: 1, code: 'N/A', countryId: 1, name: 'N/A' }]
          this.setFieldValue('state', this.allStates[0]);
        }
      }, (error) => {
        this.setControlTextValues();
        console.error(error);
      });
  }

  showSuccessNotification(message: string) {
    this.toastrService.success(message, this.resource, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }

  showErrorNotification(message: string) {
    this.toastrService.error(message, this.resource, {
      timeOut: 3000,
      closeButton: true,
      positionClass: 'toast-top-center',
    });
  }

  //get IDM Id from IDN combobox
  selectIDMIdnId(idxIDMName: string) {
    let existingIDM = this.allTenantsIDM.find(x => x.name.toLowerCase() == idxIDMName.toLowerCase())
    if (existingIDM != null) {

      //Get the current GUID Id Tenant from IDM to manage
      //Create/Update/AssignClient requests in IDM by Web API
      this.selectedIdnGuid = existingIDM.id;
      this.selectedTentatIDMValue = existingIDM.name;
      this.idnInfoForm.controls.idnCode.markAsPristine();
      this.idnInfoForm.controls.idnCode.markAsUntouched();
      //Set Tenant code
      this.setFieldValue('idnCode', existingIDM?.code);

    }
  }

  private setControlTextValues() {
    this.lblCreateText = this.createMode ? this.resourceKeyValue.idnCreatePage : this.resourceKeyValue.idnEditPage;
    this.btnCreateText = this.resourceKeyValue.idnSaveIDN;
    this.btnCancelText = this.resourceKeyValue.idnCancelSaveIDN;
    this.successMessageOnCreate = this.resourceKeyValue.idnSuccessMessageOnCreate;
    this.successMessageOnUpdate = this.resourceKeyValue.idnSuccessMessageOnUpdate;
    this.errorMessageOnCreate = this.resourceKeyValue.idnErrorMessageOnCreate;
    this.errorMessageOnUpdate = this.resourceKeyValue.idnErrorMessageOnUpdate;
  }

  createRequestObject(formGroup: UntypedFormGroup): IIDN {
    let requestObj = {
      id: this.getTrimValue(formGroup, 'id'),
      idmIdnId: this.selectedIdnGuid,
      idmIdnCode: this.getTrimValue(formGroup, 'idnCode'),
      name: this.createMode ? this.getTrimValue(formGroup, 'name') : this.getTrimValue(formGroup, 'idnName'),
      accountNumber: this.getTrimValue(formGroup, 'accountNumber'),
      countryId: this.getTrimValue(formGroup, 'countryId'),
      address1: this.getTrimValue(formGroup, 'address1'),
      address2: this.getTrimValue(formGroup, 'address2'),
      city: this.getTrimValue(formGroup, 'city'),
      state: this.getTrimValue(formGroup, 'state'),
      zipcode: this.getTrimValue(formGroup, 'zipCode'),
      hospitalFacilities: []
    };

    return requestObj;
  }

  getTrimValue(formGroup: UntypedFormGroup, fieldName: string) {
    const val = formGroup.get(fieldName).value;
    if (typeof val === "number") {
      return val;
    }
    else if (val !== undefined && val !== null) {
      return val.trim();
    }
    return "";
  }

}
